//Built-in libraries
import React, { Suspense, useEffect, useState } from 'react';

//Third party libraries
import { Navigate, Route, Routes, useParams } from 'react-router';
import ErrorCard from '@/components/UI/ErrorCard';
//Custom components
import LoadingProjectDetail from '@/components/UI/loading/LoadingProjectDetail';
import LoadingProjectPhase from '@/components/UI/loading/LoadingProjectPhase';

//Services

//Assets andd Miscellaneous
import type { GetProjectTeamMembers, InternalAndExternalTeam, ProjectPhases } from '@/types/Project.ts';

//Component Begin

import { useQuery } from '@tanstack/react-query';
import { useSelector } from 'react-redux';
import { getProjectTeamMembers } from '@/api/Sherpa/Project/modules/ProjectTeam';
import { HasPermission } from '@/components/Permission/HasPermission';
import Unauthorized from '@/components/Unauthorized';
import { useProject } from '@/hooks/ReactQueryHooks/useProjectQuery';
import { authSelector } from '@/store';
//Pages
import PageNotFound from '../../../../pages/PageNotFound';
import ProjectHeader from './components/ProjectHeader';
import ProjectNav from './components/ProjectNav';
import ProjectNotFound from './ProjectNotFound';
import type { IProjectPhaseNav } from './types';

//Lazy Loading
const Dashboard = React.lazy(() => import('./Dashboard'));
const Acquisition = React.lazy(() => import('./Acquisition'));
const Finance = React.lazy(() => import('./Finance'));
const Procurement = React.lazy(() => import('./Procurement'));
const Design = React.lazy(() => import('./Design'));
const Development = React.lazy(() => import('./Development'));
const Construction = React.lazy(() => import('./Construction'));

const phases: ProjectPhases[] = ['acquisition', 'construction', 'design', 'development', 'finance', 'procurement', 'all'];

type ProjectMembersAllPhase = {
  [K in ProjectPhases]: InternalAndExternalTeam;
};

type ProjectPhaseAccess = {
  [K in ProjectPhases]: boolean;
};

const defaultAccess: ProjectPhaseAccess = {
  acquisition: false,
  construction: false,
  design: false,
  development: false,
  finance: false,
  procurement: false,
  all: false,
};

const defaultValue: ProjectMembersAllPhase = {
  acquisition: { internal: [], external: [], svntVendors: [] },
  construction: { internal: [], external: [], svntVendors: [] },
  design: { internal: [], external: [], svntVendors: [] },
  development: { internal: [], external: [], svntVendors: [] },
  finance: { internal: [], external: [], svntVendors: [] },
  procurement: { internal: [], external: [], svntVendors: [] },
  all: { internal: [], external: [], svntVendors: [] },
};

export const ProjectDetails = () => {
  const { projectId } = useParams();
  const [hasPhaseAccess, setHasPhaseAccess] = useState<ProjectPhaseAccess>(defaultAccess);
  const { data: currentUser } = useSelector(authSelector);
  const fetchProject = useProject({
    projectId: parseInt(projectId!, 10),
  });
  const project = fetchProject?.data?.data;

  const getProjectMembersData = async (data: GetProjectTeamMembers): Promise<Partial<ProjectMembersAllPhase>> => {
    const res = await getProjectTeamMembers({
      projectId: data.projectId,
      params: { phase: data.phase === 'all' ? undefined : data.phase },
    });
    return {
      [data.phase || 'all']: res.data,
    };
  };
  const projectMembersQuery = useQuery<ProjectMembersAllPhase>(
    ['project-members-all', project?.id],
    async () => {
      if (!project?.id) return {} as ProjectMembersAllPhase;
      const data = await Promise.all([
        getProjectMembersData({ projectId: project.id }),
        ...phases.map((phase) =>
          getProjectMembersData({
            projectId: project.id,
            phase,
          })
        ),
      ]);
      return data.reduce<ProjectMembersAllPhase>((acc, next) => {
        acc = { ...acc, ...next };
        return acc;
      }, defaultValue);
    },
    {
      keepPreviousData: true,
      staleTime: 30000,
      enabled: !!project?.id,
    }
  );
  const projectMembersQueryData = projectMembersQuery.data || defaultValue;
  const projectPhase: IProjectPhaseNav[] = [
    {
      component: Acquisition,
      path: 'acquisition',
    },
    {
      component: Finance,
      path: 'finance',
    },
    {
      component: Procurement,
      path: 'procurement',
    },
    {
      component: Development,
      path: 'development',
    },
    {
      component: Design,
      path: 'design',
    },
    {
      component: Construction,
      path: 'construction',
    },
  ];
  useEffect(() => {
    const { all, ...rest } = projectMembersQueryData;

    const isProjectLead = project?.projectLead?.id === currentUser.id;
    if (isProjectLead || currentUser.role.toUpperCase() === 'SUPER ADMIN') {
      // if current user is project lead give access to all tabs
      setHasPhaseAccess({
        acquisition: true,
        construction: true,
        design: true,
        development: true,
        finance: true,
        procurement: true,
        all: true,
      });
    } else {
      // if current user is not project lead check for all the phases if the current user is member of
      const access = Object.keys(rest).reduce<ProjectPhaseAccess>((acc, phase) => {
        const { internal, external } = projectMembersQueryData[phase as keyof typeof projectMembersQueryData] || {
          internal: [],
          external: [],
        };
        acc = {
          ...acc,
          [phase]:
            // if current user is member of current phase make that phase access true
            internal.findIndex((member) => currentUser.id === member.id) !== -1 ||
            external.findIndex((member) => currentUser.id === member.id) !== -1,
        };
        return acc;
      }, defaultAccess);
      setHasPhaseAccess(access);
    }
  }, [projectMembersQueryData, currentUser.id, currentUser.role.toUpperCase, project?.projectLead?.id]);

  return (
    <HasPermission of='READ_PROJECT' fallback={<Unauthorized />}>
      {fetchProject.isLoading && <LoadingProjectDetail />}
      {fetchProject?.error?.response?.status === 404 && !fetchProject.isLoading && <ProjectNotFound />}
      {fetchProject?.error?.response?.status === 500 && !fetchProject.isLoading && (
        <ErrorCard
          errorMsg='Uh oh! We are unable to fetch your requested project at this time. Please make sure your are accessing correct project and try again.'
          errorTitle='Project Fetch Error'
        />
      )}
      {project && !fetchProject.isLoading && (
        <div className='flex flex-col mx-auto'>
          <ProjectHeader />
          <ProjectNav hasPhaseAccess={hasPhaseAccess} />
          <Suspense fallback={<LoadingProjectPhase />}>
            <Routes>
              <Route path='/' element={<Navigate replace to='dashboard' />} />
              <Route path='dashboard' element={<Dashboard />} />
              {projectPhase.map((phase) => {
                if (hasPhaseAccess[phase.path as keyof typeof hasPhaseAccess]) {
                  return <Route key={phase.path as string} path={`${phase.path}`} element={<phase.component />} />;
                }
                return null;
              })}
              <Route path='*' element={<PageNotFound />} />
            </Routes>
          </Suspense>
        </div>
      )}
    </HasPermission>
  );
};
